import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getActiveJobs } from '../services/jobService';
import { getSavedJobIds } from '../services/savedJobsService';
import { getProfileStats, getSeekerProfile } from '../services/seekerProfileService';
import { applyForJob, getUserApplications } from '../services/applicationService';
import { requestNotificationPermission } from '../services/fcmService';
import { getUserActiveSubscription, createSubscription } from '../services/subscriptionService';
import { getPackagesByType, PACKAGE_TYPES } from '../services/packageService';
import { initiateRazorpayPayment, calculateGST } from '../services/razorpayService';
import { createPaymentRecord, markPaymentSuccess, markPaymentFailed } from '../services/paymentService';
import JobCard from '../components/JobCard';
import JobApplicationModal from '../components/JobApplicationModal';
import LoadingSpinner from '../components/LoadingSpinner';
import Footer from '../components/Footer';

import ContentWithAds from '../components/ContentWithAds';

const JobSeekerDashboard = ({ user, userData }) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [showJobApplicationModal, setShowJobApplicationModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applying, setApplying] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [filters, setFilters] = useState({
    jobType: '',
    workMode: '',
    experienceLevel: '',
    location: '',
    search: ''
  });
  const [savedJobsCount, setSavedJobsCount] = useState(0);
  const [profileStats, setProfileStats] = useState({ completionPercentage: 0, isComplete: false, hasProfile: false });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [applicationsThisMonth, setApplicationsThisMonth] = useState(0);
  const [subscription, setSubscription] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showInfoFormModal, setShowInfoFormModal] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);
  const jobsPerPage = 10;

  useEffect(() => {
    const init = async () => {
      await fetchJobs();
      setInitialLoading(false);
      if (user) {
        fetchSavedJobsCount();
        fetchProfileStats();
        fetchApplicationsThisMonth();
        await fetchSubscription();
        initializeFCM();
      }
    };
    init();
  }, [user]);

  // Handle showing the modal if no subscription is found
  useEffect(() => {
    if (user && !initialLoading && subscriptionLoaded && subscription === null) {
      const timer = setTimeout(() => {
        setShowInfoFormModal(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [user, initialLoading, subscriptionLoaded, subscription]);

  // Debounce location input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (locationInput !== filters.location) {
        handleFilterChange({ ...filters, location: locationInput });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [locationInput]);

  const initializeFCM = async () => {
    try {
      // Check if FCM token exists, if not request permission and save
      const result = await requestNotificationPermission(user.uid);
      if (result.success) {
        console.log('FCM token initialized successfully');
      }
    } catch (error) {
      console.error('Error initializing FCM:', error);
    }
  };

  const fetchJobs = async (page = 1) => {
    console.log('🚀 [JobSeekerDashboard] fetchJobs called for page:', page);
    setLoading(true);
    try {
      console.log('🔄 [JobSeekerDashboard] Fetching active jobs...');

      const result = await getActiveJobs(filters, {
        limit: jobsPerPage,
        offset: (page - 1) * jobsPerPage
      });
      console.log('📊 [JobSeekerDashboard] Active jobs result:', result);

      if (result.success) {
        console.log('✅ [JobSeekerDashboard] Setting jobs:', result.jobs);
        setJobs(result.jobs);
        setTotalJobs(result.total || result.jobs.length);
        setCurrentPage(page);
      } else {
        console.error('❌ [JobSeekerDashboard] Failed to fetch active jobs:', result.error);
      }
    } catch (error) {
      console.error('❌ [JobSeekerDashboard] Error fetching jobs:', error);
      console.error('❌ [JobSeekerDashboard] Error stack:', error.stack);
    } finally {
      console.log('🏁 [JobSeekerDashboard] fetchJobs completed, setting loading to false');
      setLoading(false);
    }
  };

  const handleFilterChange = async (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
    setLoading(true);
    try {
      const result = await getActiveJobs(newFilters, {
        limit: jobsPerPage,
        offset: 0
      });
      if (result.success) {
        setJobs(result.jobs);
        setTotalJobs(result.total || result.jobs.length);
      }
    } catch (error) {
      console.error('Error filtering jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    handleFilterChange({ ...filters, search: searchTerm });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (page) => {
    fetchJobs(page);
  };

  const totalPages = Math.ceil(totalJobs / jobsPerPage);

  const fetchSavedJobsCount = async () => {
    try {
      const result = await getSavedJobIds(user.uid);
      if (result.success) {
        setSavedJobsCount(result.jobIds.length);
      }
    } catch (error) {
      console.error('Error fetching saved jobs count:', error);
    }
  };

  const handleApplyClick = (job, setJobApplying, setJobApplied) => {
    setSelectedJob(job);
    setShowJobApplicationModal(true);
  };

  const handleConfirmApplication = async (applicationData) => {
    if (!selectedJob) return;

    setApplying(true);
    try {
      const result = await applyForJob(selectedJob.id, user.uid, applicationData);

      if (result.success) {
        setShowJobApplicationModal(false);
        setSelectedJob(null);
        setShowSuccessAnimation(true);

        // Hide success animation after 3 seconds
        setTimeout(() => {
          setShowSuccessAnimation(false);
        }, 3000);

        // Refresh jobs to update applied status and applications count
        fetchJobs();
        fetchApplicationsThisMonth();
      } else {
        alert(result.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error applying for job:', error);
      alert('Failed to submit application. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const fetchProfileStats = async () => {
    try {
      const profileResult = await getSeekerProfile(user.uid);
      const statsResult = await getProfileStats(user.uid);

      setProfileStats({
        hasProfile: profileResult.success && profileResult.profile,
        completionPercentage: statsResult.success ? statsResult.stats.completionPercentage : 0,
        isComplete: statsResult.success ? statsResult.stats.completionPercentage >= 85 : false
      });
    } catch (error) {
      console.error('Error fetching profile stats:', error);
    }
  };

  const fetchApplicationsThisMonth = async () => {
    try {
      const result = await getUserApplications(user.uid);
      if (result.success) {
        // Get current month and year
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        // Filter applications from this month
        const thisMonthApplications = result.applications.filter(app => {
          const appliedDate = app.appliedAt;
          if (!appliedDate) return false;

          const appMonth = appliedDate.getMonth();
          const appYear = appliedDate.getFullYear();

          return appMonth === currentMonth && appYear === currentYear;
        });

        setApplicationsThisMonth(thisMonthApplications.length);
      }
    } catch (error) {
      console.error('Error fetching applications this month:', error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const result = await getUserActiveSubscription(user.uid);
      if (result.success) {
        setSubscription(result.subscription);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setSubscriptionLoaded(true);
    }
  };

  const handleProfileSubmitAndPay = async (e) => {
    e.preventDefault();
    setIsProcessingPayment(true);

    try {
      const packagesResult = await getPackagesByType(PACKAGE_TYPES.JOB_SEEKER);
      if (!packagesResult.success || !packagesResult.packages.length) {
        alert("No packages available currently.");
        setIsProcessingPayment(false);
        return;
      }

      const pkg = packagesResult.packages.find(p => p.price > 0) || packagesResult.packages[0];

      if (!pkg || pkg.price === 0) {
        alert("Paid package not found. Please visit the packages page.");
        setIsProcessingPayment(false);
        return;
      }

      const gstCalculation = calculateGST(pkg.price);
      const totalAmount = gstCalculation.totalAmount;
      const baseAmount = pkg.price;

      const paymentData = {
        userId: user.uid,
        packageId: pkg.id,
        packageName: pkg.name,
        amount: totalAmount,
        currency: 'INR',
        gstAmount: gstCalculation.gstAmount,
        baseAmount: baseAmount,
        totalAmount: totalAmount,
        userEmail: userData?.email || user.email,
        userName: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : user.displayName,
        userPhone: userData?.phone || '',
      };

      const paymentResult = await createPaymentRecord(paymentData);
      if (!paymentResult.success) throw new Error('Failed to create payment record');

      const firebasePaymentId = paymentResult.paymentId;

      await initiateRazorpayPayment({
        amount: totalAmount,
        currency: 'INR',
        packageName: pkg.name,
        packageId: pkg.id,
        userEmail: userData?.email || user.email,
        userName: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : user.displayName,
        userPhone: userData?.phone || '',
        userId: user.uid,
        onSuccess: async (paymentResponse) => {
          await markPaymentSuccess(firebasePaymentId, paymentResponse);

          const subscriptionData = {
            userId: user.uid,
            packageId: pkg.id,
            packageName: pkg.name,
            packageType: pkg.type,
            price: pkg.price,
            duration: pkg.duration,
            features: pkg.features || [],
            maxApplications: pkg.maxApplications || 0,
            resumeTemplates: pkg.resumeTemplates || 0,
            profileBoost: pkg.profileBoost || false,
            userType: 'jobseeker',
            paymentId: firebasePaymentId,
            razorpayPaymentId: paymentResponse.paymentId,
            razorpayOrderId: paymentResponse.orderId || null,
          };

          const subscriptionResult = await createSubscription(subscriptionData);
          if (subscriptionResult.success) {
            alert(`✅ Successfully purchased ${pkg.name}!`);
            setShowInfoFormModal(false);
            fetchSubscription();
          } else {
            alert('Payment successful but failed to activate subscription. Please contact support.');
          }
          setIsProcessingPayment(false);
        },
        onFailure: async (error) => {
          await markPaymentFailed(firebasePaymentId, error.error || 'Payment failed');
          if (!error.cancelled) alert('Payment failed: ' + (error.error || 'Unknown error'));
          setIsProcessingPayment(false);
        },
      });

    } catch (error) {
      console.error('Payment Error:', error);
      alert('Error initiating payment: ' + error.message);
      setIsProcessingPayment(false);
    }
  };

  if (initialLoading) {
    console.log('🔄 [JobSeekerDashboard] Initial loading...');
    return <LoadingSpinner />;
  }

  console.log('🎨 [JobSeekerDashboard] Rendering with jobs:', jobs);
  console.log('🎨 [JobSeekerDashboard] Jobs length:', jobs.length);

  return (
    <>
      <div className="min-h-screen bg-gray-50 pb-20 lg:pb-0">
        {/* Floating Asymmetric Hero Panel */}
        <div className="mx-4 sm:mx-8 my-6 relative overflow-hidden bg-gradient-to-br from-[#0e3a61] to-[#08233c] rounded-[2.5rem] border border-primary-950/20 shadow-xl">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-72 h-72 bg-white opacity-10 rounded-full -translate-x-36 -translate-y-36"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full translate-x-48 -translate-y-48"></div>
            <div className="absolute bottom-0 left-1/2 w-80 h-80 bg-white opacity-10 rounded-full translate-x-40 translate-y-40"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Greetings, <span className="text-accent-300">{userData?.firstName}!</span>
              </h1>
              <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
                Access curated pathways and orchestrate your next major career move
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/jobs')}
                  className="px-8 py-3 bg-white text-primary-600 font-semibold rounded-xl hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Browse Openings
                </button>
                <button
                  onClick={() => navigate('/seeker-profile')}
                  className="hidden sm:block px-8 py-3 bg-accent-600 text-white font-semibold rounded-xl hover:bg-accent-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  {!profileStats.hasProfile ? 'Design Profile' :
                    profileStats.completionPercentage < 85 ? 'Finalize Profile' : 'Refine Profile'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content with Left and Right Ad Sidebars */}
        <ContentWithAds>
          <div className="max-w-7xl mx-auto  sm:px-6 lg:px-8 py-6 mt-4 sm:py-12 sm:mt-16 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-3 ">
              {/* Sidebar: Stats + Quick Actions */}
              <div className="lg:col-span-1 space-y-6">
                <div className="bg-white rounded-[2rem] rounded-tr-[3.5rem] rounded-bl-[3.5rem] shadow-lg p-6 border border-slate-100 hidden lg:block">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2m0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    Professional Snapshot
                  </h2>
                  <div className="space-y-4">
                    {/* Subscription Status */}
                    <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-indigo-900">Membership Level</span>
                        <span className="px-2 py-1 bg-white text-indigo-700 text-xs font-bold rounded shadow-sm border border-indigo-100">
                          {subscription ? subscription.packageName : 'Basic Access'}
                        </span>
                      </div>

                      {subscription && (
                        <>
                          <div className="mb-2">
                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Applications</span>
                              <span className="font-semibold text-indigo-700">
                                {applicationsThisMonth} / {subscription.maxApplications === 999999 ? '∞' : subscription.maxApplications}
                              </span>
                            </div>
                            <div className="w-full bg-white rounded-full h-1.5 overflow-hidden">
                              <div
                                className="bg-indigo-500 h-1.5 rounded-full"
                                style={{ width: `${subscription.maxApplications === 999999 ? 0 : Math.min((applicationsThisMonth / (subscription.maxApplications || 1)) * 100, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                          <div className="text-xs text-indigo-800 flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Expires: {subscription.endDate?.toDate ? subscription.endDate.toDate().toLocaleDateString() : new Date(subscription.endDate).toLocaleDateString()}
                          </div>
                        </>
                      )}

                      {!subscription && (
                        <div className="mt-2">
                          <button
                            onClick={() => navigate('/packages')}
                            className="w-full py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-xs font-semibold rounded-lg shadow-md transition-all duration-300"
                          >
                            Accelerate Membership
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between p-4 bg-primary-50 rounded-xl border border-primary-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-600">Active Submissions</p>
                          <p className="text-xs text-gray-500">This month</p>
                        </div>
                      </div>
                      <span className="text-2xl font-bold text-primary-600">{applicationsThisMonth}</span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-xl border border-secondary-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-secondary-600 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-600">Employer Engagements</p>
                          <p className="text-xs text-gray-500">This week</p>
                        </div>
                      </div>
                      <span className="text-2xl font-bold text-secondary-600">0</span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-accent-50 rounded-xl border border-accent-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-accent-600 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-600">Bookmarked Pursuits</p>
                          <p className="text-xs text-gray-500">Total saved</p>
                        </div>
                      </div>
                      <span className="text-2xl font-bold text-accent-600">{savedJobsCount}</span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-primary-50 rounded-xl border border-primary-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-600">Fidelity Metric</p>
                          <p className="text-xs text-gray-500">
                            {profileStats.isComplete ? 'Operational' : 'Pending Action'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-2xl font-bold ${profileStats.isComplete ? 'text-secondary-600' : 'text-accent-600'}`}>
                          {profileStats.completionPercentage}%
                        </span>
                        {!profileStats.isComplete && (
                          <div className="w-16 bg-gray-200 rounded-full h-1.5 mt-1">
                            <div
                              className="bg-accent-500 h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${profileStats.completionPercentage}%` }}
                            ></div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions - Desktop Only */}
                <div className="bg-white rounded-[2rem] rounded-tr-[3.5rem] rounded-bl-[3.5rem] shadow-lg p-6 border border-slate-100 hidden lg:block">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Key Hub</h2>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <Link
                      to="/jobs"
                      className="group flex items-center px-4 py-3 bg-secondary-50 border border-secondary-200 rounded-xl shadow-sm text-sm font-semibold text-secondary-700 hover:bg-secondary-100 hover:border-secondary-300 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                    >
                      <svg className="w-4 h-4 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                      </svg>
                      Explore Opportunities
                    </Link>
                    <Link
                      to="/seeker-profile"
                      className="group flex items-center px-4 py-3 bg-primary-50 border border-primary-200 rounded-xl shadow-sm text-sm font-semibold text-primary-700 hover:bg-primary-100 hover:border-primary-300 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                    >
                      <svg className="w-4 h-4 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {!profileStats.hasProfile ? 'Design Profile' :
                        profileStats.completionPercentage < 85 ? 'Finalize Profile' : 'Refine Profile'}
                    </Link>
                    <Link
                      to="/saved-jobs"
                      className="group flex items-center px-4 py-3 bg-accent-50 border border-accent-200 rounded-xl shadow-sm text-sm font-semibold text-accent-700 hover:bg-accent-100 hover:border-accent-300 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                    >
                      <svg className="w-4 h-4 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      Bookmarked Pursuits
                    </Link>
                    <Link
                      to="/application-history"
                      className="group flex items-center px-4 py-3 bg-primary-50 border border-primary-200 rounded-xl shadow-sm text-sm font-semibold text-primary-700 hover:bg-primary-100 hover:border-primary-300 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                    >
                      <svg className="w-4 h-4 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Active Submissions
                    </Link>
                    <Link
                      to="/notification-settings"
                      className="group flex items-center px-4 py-3 bg-secondary-50 border border-secondary-200 rounded-xl shadow-sm text-sm font-semibold text-secondary-700 hover:bg-secondary-100 hover:border-secondary-300 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                    >
                      <svg className="w-4 h-4 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      Alert Rules
                    </Link>
                  </div>
                </div>
              </div>

              {/* Main Content */}
              <div className="lg:col-span-2 space-y-3 sm:space-y-6">
                {/* Search and Filters */}
                <div className="bg-white rounded-[2rem] shadow-md p-4 sm:p-6 border border-slate-100">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Query Opportunities Database</h2>
                  </div>

                  {/* Search Bar & Filter Toggle */}
                  <div className="mb-6 flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Filter by target position, corporate name, or industry..."
                        className="block w-full pl-10 pr-24 sm:pr-28 py-3 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                      <div className="absolute inset-y-0 right-0 pr-1.5 flex items-center">
                        <button
                          onClick={handleSearch}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          Search
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className={`px-4 py-3 border rounded-xl transition-all duration-200 flex items-center justify-center gap-2 hover:shadow-md ${showFilters ? 'bg-blue-50 border-blue-200 text-blue-600' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                      <span className="font-medium">Parameters</span>
                    </button>
                  </div>

                  {/* Filters */}
                  {showFilters && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fadeIn">
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">Job Type</label>
                        <select
                          value={filters.jobType}
                          onChange={(e) => handleFilterChange({ ...filters, jobType: e.target.value })}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                        >
                          <option value="">All Job Types</option>
                          <option value="full-time">Full-time</option>
                          <option value="part-time">Part-time</option>
                          <option value="contract">Contract</option>
                          <option value="internship">Internship</option>
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">Work Mode</label>
                        <select
                          value={filters.workMode}
                          onChange={(e) => handleFilterChange({ ...filters, workMode: e.target.value })}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                        >
                          <option value="">All Work Modes</option>
                          <option value="Work From Home">Work From Home</option>
                          <option value="In Field">In Field</option>
                          <option value="Work From Office">Work From Office</option>
                          <option value="Hybrid">Hybrid</option>
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">Experience Level</label>
                        <select
                          value={filters.experienceLevel}
                          onChange={(e) => handleFilterChange({ ...filters, experienceLevel: e.target.value })}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                        >
                          <option value="">All Experience Levels</option>
                          <option value="entry">Entry Level</option>
                          <option value="mid">Mid Level</option>
                          <option value="senior">Senior Level</option>
                          <option value="executive">Executive</option>
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">Location</label>
                        <input
                          type="text"
                          placeholder="Enter city or remote"
                          value={locationInput}
                          onChange={(e) => setLocationInput(e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Available Jobs */}
                <div className="bg-white rounded-[2rem] shadow-lg border border-slate-100 flex flex-col h-auto min-h-[400px] lg:h-[600px]">
                  <div className="px-4 py-4 sm:px-6 sm:py-5 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                        </svg>
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Curated Openings</h2>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">{totalJobs} total jobs</span>
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    </div>
                  </div>

                  {/* Scrollable Jobs Container */}
                  <div className="flex-1 lg:overflow-y-auto p-4 sm:p-6 relative">
                    {loading && (
                      <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-10">
                        <LoadingSpinner />
                      </div>
                    )}
                    {jobs.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="text-gray-400 mb-4">
                          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No matches returned</h3>
                        <p className="text-gray-600 mb-4">Adjust parameters to broaden scope, or query again shortly.</p>
                        <button
                          onClick={() => handleFilterChange({ jobType: '', workMode: '', experienceLevel: '', location: '' })}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                        >
                          Clear Filters
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {jobs.map((job) => (
                          <JobCard
                            key={job.id}
                            job={job}
                            showActions={false}
                            user={user}
                            userData={userData}
                            onJobSaved={() => fetchSavedJobsCount()}
                            onJobUnsaved={() => fetchSavedJobsCount()}
                            onApplyClick={handleApplyClick}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700">
                          Page {currentPage} of {totalPages}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({((currentPage - 1) * jobsPerPage) + 1}-{Math.min(currentPage * jobsPerPage, totalJobs)} of {totalJobs})
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-1.5 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }

                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md ${pageNum === currentPage
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                  }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1.5 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </ContentWithAds>
      </div>
      {/* Success Animation */}
      {
        showSuccessAnimation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300">
            <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl transform transition-all duration-300 scale-100">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">🎉 Application Submitted!</h3>
              <p className="text-gray-600 mb-4">Your application has been sent successfully. Good luck!</p>
              <div className="flex items-center justify-center space-x-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        )
      }

      {/* Job Application Modal */}
      <JobApplicationModal
        job={selectedJob}
        isOpen={showJobApplicationModal}
        onClose={() => {
          setShowJobApplicationModal(false);
          setSelectedJob(null);
        }}
        onConfirm={handleConfirmApplication}
        applying={applying}
      />

      {/* Information Form Modal */}
      {showInfoFormModal && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden relative transform transition-all duration-300 scale-100 border border-white/20">
            {/* Header Background */}
            <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-br from-primary-600 to-primary-800 opacity-10"></div>

            <button
              onClick={() => setShowInfoFormModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 bg-white hover:bg-gray-100 rounded-full p-2 transition-all duration-200 shadow-sm z-10"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>

            <div className="p-8 relative z-10">
              <div className="mb-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-tr from-primary-600 to-accent-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg transform -rotate-3 hover:rotate-0 transition-transform duration-300">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                  </svg>
                </div>
                <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">Complete Your Profile</h2>
                <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">Take a moment to provide these details to unlock your full potential on our platform.</p>
              </div>

              <form className="space-y-5" onSubmit={handleProfileSubmitAndPay}>
                {/* Address */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Address</label>
                  <div className="relative">
                    <div className="absolute top-3 left-3 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    </div>
                    <textarea className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm transition-all duration-200 resize-none" rows="2" placeholder="Enter your full address"></textarea>
                  </div>
                </div>

                {/* Mobile Numbers - Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Mobile Number</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                      </div>
                      <input type="tel" pattern="[0-9]{10}" maxLength="10" title="Please enter exactly 10 digits" onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm transition-all duration-200" placeholder="10-digit mobile no." />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Reference Mobile</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                      </div>
                      <input type="tel" pattern="[0-9]{10}" maxLength="10" title="Please enter exactly 10 digits" onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm transition-all duration-200" placeholder="Optional 10-digit no." />
                    </div>
                  </div>
                </div>

                {/* Resume Upload - Custom styled */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Resume Upload</label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-colors duration-200 group cursor-pointer relative overflow-hidden">
                    <input id="file-upload" name="file-upload" type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" accept=".pdf,.doc,.docx" onChange={(e) => {
                      const fileName = e.target.files[0]?.name;
                      const displayEl = document.getElementById('file-upload-display');
                      if (fileName && displayEl) {
                        displayEl.innerHTML = `<span class="text-primary-600 font-medium">${fileName}</span>`;
                      }
                    }} />
                    <div className="space-y-1 text-center pointer-events-none">
                      <svg className="mx-auto h-12 w-12 text-gray-400 group-hover:text-primary-500 transition-colors" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div className="flex text-sm text-gray-600 justify-center">
                        <span className="relative font-medium text-primary-600 focus-within:outline-none px-1" id="file-upload-display">
                          Upload a file <span className="text-gray-500 font-normal">or drag and drop</span>
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">PDF, DOC, DOCX up to 10MB</p>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <button type="submit" disabled={isProcessingPayment} className={`w-full py-3.5 px-4 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform flex justify-center items-center gap-2 ${isProcessingPayment ? 'opacity-70 cursor-not-allowed scale-100' : 'hover:-translate-y-1'}`}>
                    <span>{isProcessingPayment ? 'Processing Payment...' : 'Submit & Continue'}</span>
                    {!isProcessingPayment && <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation - Quick Actions */}


      {/* Footer - Hidden on mobile */}
      <div className="hidden lg:block">
        <Footer />
      </div>
    </>
  );
};

export default JobSeekerDashboard;
